import { useId } from "react";
import { ExternalLink } from "lucide-react";
import { KlipboardCrmInline } from "@/components/logos/KlipboardCrmInline";
import { VectaFull } from "@/components/logos/VectaFull";
import type { AppRoutes } from "@/types/domain";
import { popupCenter } from "@/utils/popup";

const linkCls = "flex min-h-[44px] min-w-0 items-center gap-1.5 rounded px-1 py-2 text-[13px] font-normal leading-normal text-gray-600 no-underline hover:bg-gray-100 hover:text-gray-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 md:min-h-[32px] md:py-1";
const headingCls = "m-0 mb-1 px-1 text-[12px] font-medium leading-4 tracking-normal text-gray-600";
const listCls = "list-none m-0 p-0";

type MegaFooterProps = {
  routes?: Partial<AppRoutes>;
};

export const MegaFooter = ({ routes }: MegaFooterProps) => {
  const descriptionId = useId();
  const newTabDescriptionId = `${descriptionId}-new-tab`;
  const newWindowDescriptionId = `${descriptionId}-new-window`;
  const r = {
    myDiary:           routes?.myDiary           ?? "MyDiary.aspx",
    userPreferences:   routes?.userPreferences   ?? "UserPref.aspx",
    accountSettings:   routes?.accountSettings   ?? "/AccountSettings",
    notificationsList: routes?.notificationsList ?? "NotificationsList.aspx",
  };

  return (
    <footer className="mt-auto shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3 font-sans text-[13px] leading-normal md:px-8 print:hidden">
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4">
        <div className="flex flex-col items-start gap-1">
          <a
            href="https://vecta.net/en-gb/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Klipboard CRM"
            aria-describedby={newTabDescriptionId}
            title="Opens in a new tab"
            className="flex min-h-[44px] items-center gap-2 rounded px-1 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 md:min-h-[32px]"
          >
            <KlipboardCrmInline iconSize={21} textColor="#29335C" className="forced-colors:[&_*]:fill-[CanvasText]" />
            <ExternalLink size={12} className="shrink-0 text-gray-600" aria-hidden="true" />
          </a>
          <p className="m-0 px-1 text-[12px] leading-4 text-gray-600">
            Powered by{" "}
            <span className="inline-flex items-center gap-1.5 align-bottom forced-colors:[&_svg_*]:fill-[CanvasText]">
              <VectaFull logoWidth={18} textClassName="text-gray-600 font-semibold text-[15px] tracking-tight" />
            </span>
            <br />Sales Analytics, BI &amp; CRM
          </p>
        </div>
        <nav aria-label="Footer utilities" className="grid w-full min-w-0 grid-cols-2 gap-x-3 gap-y-4 md:flex md:w-auto md:flex-wrap md:gap-x-8">
          <div className="min-w-0">
            <h2 className={headingCls}>Workspace</h2>
            <ul className={`${listCls} md:grid md:grid-cols-2 md:gap-x-3`}>
              <li><a href={r.myDiary} className={linkCls}>My Diary</a></li>
              <li><a href={r.notificationsList} className={linkCls}>Notifications</a></li>
              <li>
                <a href={r.userPreferences} className={linkCls} aria-describedby={newWindowDescriptionId} title="Opens in a new window" onClick={(e) => { e.preventDefault(); popupCenter(r.userPreferences, "UserPreferences", 1024, 768); }}>
                  My Preferences
                  <ExternalLink size={12} className="shrink-0" aria-hidden="true" />
                </a>
              </li>
              <li className="hidden md:block"><a href={r.accountSettings} className={linkCls}>Account Settings</a></li>
            </ul>
          </div>
          <div className="min-w-0">
            <h2 className={headingCls}>Help &amp; support</h2>
            <ul className={`${listCls} md:flex md:flex-wrap md:gap-x-3`}>
              <li>
                <a href="https://vecta.net/en-gb/support-service-second-to-none" target="_blank" rel="noopener noreferrer" className={linkCls} aria-describedby={newTabDescriptionId} title="Opens in a new tab">
                  Support Centre
                  <ExternalLink size={12} className="shrink-0" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a href="https://vecta.net/en-gb/faqs-current-and-future-users" target="_blank" rel="noopener noreferrer" className={linkCls} aria-describedby={newTabDescriptionId} title="Opens in a new tab">
                  FAQs
                  <ExternalLink size={12} className="shrink-0" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a href="https://vecta.net/en-gb/contact-us" target="_blank" rel="noopener noreferrer" className={linkCls} aria-describedby={newTabDescriptionId} title="Opens in a new tab">
                  Contact Us
                  <ExternalLink size={12} className="shrink-0" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </nav>
      </div>
      <span id={newTabDescriptionId} className="sr-only">Opens in a new tab</span>
      <span id={newWindowDescriptionId} className="sr-only">Opens in a new window</span>
    </footer>
  );
};
